import { DealPartnerContact, SearchResult, Guid, NaicsCode, Tier3ContactTypes } from '.';

export interface FinderFeeAgreement {
	guid: string;
	scope: string;
	tier3ContactType: Tier3ContactTypes;
}

export interface DealFundingLender {
	id: number;
	guid: string;
	bankContactGuid: string;
	lenderName: string;
	bankGuid: string;
	bankName: string;
	finderFeeAgreement: FinderFeeAgreement | null;
	amountFunded: number;
	dateFunded: Date | null;
}

export interface Deal {
	guid: string;
	companyGuid: string;
	companyName: string;
	dataRoomGuid: string;
	hubSpotId: string;
	createdOn: string;
	financialStartDate: string;
	modifiedOn: string;
	targetFundingDate: string;
	isAsapTargetDate: boolean;
	requestDescription: string;
	pricePriority: string;
	covenantPriority: string;
	maturityPriority: string;
	amortizationPriority: string;
	amountPriority: string;
	industryExpPriority: string;
	localBankPriority: string;
	bankExperiencePriority: string;
	lowerEquityPriority: string;
	avoidPersonalGuaranteePriority: string;
	totalRequestedAmount: number;
	termSheetReceiptDate: string;
	yearsInOperation: number;
	investorOwned: boolean;
	numberEmployees: number;
	mostRecentFiscalYearRevenue: number;
	haveRecurringRevenue: boolean | null;
	monthlyRecurringRevenue: number;
	annuallyRecurringRevenue: number;
	mostRecentTtmEbitda: number;
	debtScheduleJson: string;
	performaDebtIncludingFacility: number;
	amountFunded: number;
	dateFunded: string;
	isLoanRequestCompleted: boolean;
	companyClarifications: string;
	isCompanyCompleted: boolean;
	financialClarifications: string;
	isFinancialCompleted: boolean;
	lenderClarifications: string;
	isLenderCompleted: boolean;
	isQuestionsCompleted: boolean;
	companyNotes: string;
	lenderNotes: string;
	requestNotes: string;
	dealName: string;
	expirationDate: string;
	isContactsCompleted: boolean;
	requestedLenderOn: string;
	isDealStructureCompleted: boolean;
	isLockedStructures: boolean;
	nonUsBasedEmployees: number;
	isLegacyView: boolean;
	acquisitionYearsInOperation: number;
	hasLegalCouncel: boolean;
	legalFirmName: string;
	institutionalInvestor: boolean | null;
	acquisitionInstitutionalInvestor: boolean | null;
	companyFinancialNotes: string;
	acquisitionFinancialNotes: string;
	narrative: string;
	principalDetails: string;
	additionalInformationRequest: string;
	additionalInformationResponse: string;
	isDebtSectionCompleted: boolean;
	isSettingsSectionCompleted: boolean;
	hasExcludedLenders: boolean;
	hasReducedFeeLenders: boolean;
	companyLocatedInRuralArea: boolean;
	postalCode: string;
	borrowerSubmittedLoanApplicationOn: string;
	enteredMarketOn: string;
	currency: string;
	acquisitionCurrency: string;
	address1: string;
	city: string;
	acquisitionAddress1: string;
	acquisitionCity: string;
	acquisitionPostalCode: string;
	acquisitionUsBasedEmployees: number;
	acquisitionNonUsBasedEmployees: number;
	customerPurchaseOrders: boolean | null;
	purchaseOrderAverageMonthlyCost: number | null;
	acquisitionCustomerPurchaseOrders: boolean | null;
	acquisitionPurchaseOrderAverageMonthlyCost: number | null;
	welcomeEmailSentDate: string;
	applicationCompleteOverride: boolean;
	isExecutiveSummaryComplete: boolean;
	dealClosedDiscardedReason: number;
	dealClosedDiscardedNotes: string;
	includeExecutiveSummary: boolean;
	customerConcentrationsMoreThan10Percent: boolean;
	customerConcentrationDetails: string;
	acquisitionCustomerConcentrationsMoreThan10Percent: boolean;
	acquisitionCustomerConcentrationDetails: string;
	companyType: CompanyTypes;
	dealStatus: DealStatus;
	natureOfBuyer: NatureOfBuyer;
	runwayLeftInMonths: RunwayLeftInMonths;
	businessSalesType: BusinessSalesType;
	transactionType: TransactionType;
	companyFinancialStatementType: FinancialStatementType;
	acquisitionHaveRecurringRevenue: boolean | null;
	acquisitionRunwayLeftInMonths: RunwayLeftInMonths;
	acquisitionBusinessSalesType: BusinessSalesType;
	acquisitionEvaluationStage: EvaluationStage;
	acquisitionFinancialStatementType: FinancialStatementType;
	state: {
		code: string;
		name: string;
		country: string;
		countryCode: string;
	};
	naicsCode: NaicsCode;
	companyIndustry: {
		guid: string;
		name: string;
	};
	acquisitionState: {
		code: string;
		name: string;
		country: string;
		countryCode: string;
	};
	acquisitionIndustry: {
		guid: string;
		name: string;
	};
	acquisitionNaicsCode: NaicsCode;
	transactionManager: Guid | null;
	secondaryTransactionManager: Guid | null;
	lenderRelationsManager: Guid | null;
	generateLoanApplicationPdf: boolean;
	dealPartner: SearchResult | null;
	dealPartnerContact: DealPartnerContact | null;
	businessOwnershipNarrative: string;
	lastMatchingUpdateOn: Date | null;
	lastMatchingUpdateString: string;
	isPendingMatchingUpdate: boolean;
	isLenderFinanceDeal: boolean;
	isFloorplanFinanceDeal: boolean;
	hasSelectedPreference: boolean;
	hasCompanySubscription: boolean;
	hasParentCompany: boolean;
	isCompanySubscriptionRefinance: boolean;
	companySubscriptionRefinanceIncrementalAmount: number | null;
	hasGeneratedSuccessFeeInvoice: boolean;
	countryType: CountryTypes;
	acquisitionCountryType: CountryTypes | null;
	isEditingCreditAnalysis: boolean;
	isDirectIntroDeal: boolean;
	hasLaunched: boolean;
	excludedLenderCount: number;
	reducedFeeLenderCount: number;
	showMatchingBlankIssue: boolean;
	isClosedDeal: boolean;
	b2bPercent: number | null;
	b2cPercent: number | null;
	b2gPercent: number | null;
	acquisitionB2bPercent: number | null;
	acquisitionB2cPercent: number | null;
	acquisitionB2gPercent: number | null;
	matchingProcessStartedOn: Date | null;
	matchingFailedUpdated: boolean;
	fundingLenders: DealFundingLender[];
	salesPerson: Guid | null;
	isRawLead: boolean;
	isRawLeadVisible: boolean;
}

export interface SimilarDealReport {
	items: DealSearchItem[];
	matchedSummary: SimilarDealSummary;
	otherSummary: SimilarDealSummary;
}

export interface DealSearchItem {
	id: number;
	guid: string;
	dataRoomGuid: string;
	companyName: string;
	dealStartDate: string;
	dealStatus: DealStatus;
	totalTermSheets: number;
	dealSize: number;
	ttmEbitda: number;
	ttmRevenue: number;
	leverageRatio: number;
	industryName: string;
	stateCode: string;
	industryMatch: number;
	institutionLendersAccepted: number;
	dealLaunched: boolean;
	isClosedFunded: boolean;
	amountFunded: number | null;
	daysToTermSheet: number | null;
}

export interface SimilarDealSummary {
	industryMatch: boolean;
	dealsLaunched: number;
	termSheetsReceived: number;
	totalClosed: number;
	totalFundedAmount: number;
	averageLaunchToTermSheetDays: number;
}

export interface UpdateDealDto
	extends Omit<
		Partial<Deal>,
		| 'state'
		| 'naicsCode'
		| 'companyIndustry'
		| 'acquisitionState'
		| 'acquisitionNaicsCode'
		| 'acquisitionIndustry'
		| 'dealPartner'
		| 'dealPartnerContact'
	> {
	naicsCode?: string;
	stateCode?: string;
	countryCode?: string;
	companyIndustryGuid?: string;

	acquisitionNaicsCode?: string;
	acquisitionStateCode?: string;
	acquisitionCountryCode?: string;
	acquisitionIndustryGuid?: string;
	dealPartnerContactGuid?: string | null;
	sourcePage?: string | null;
}

export type CountryTypes = keyof typeof CountryTypeMap;
export const CountryTypeMap = {
	UnitedStates: 'United States',
	Canada: 'Canada',
	Mexico: 'Mexico',
	Brazil: 'Brazil',
	Europe: 'Europe',
	Other: 'Other',
};

export type CompanyTypes = keyof typeof CompanyTypeMap;

export const CompanyTypeMap = {
	Unknown: 'Unknown',
	Corporation: 'Corporation',
	Scorp: 'S-Corp',
	NonProfit: 'Non-Profit',
	LLC: 'LLC',
	Sole_Proprietor: 'Sole Proprietor',
	Partnership: 'Partnership',
	Other: 'Other',
};

export const DealStatuses = {
	Started: 'Started',
	Accepted: 'Accepted',
	Declined: 'Declined',
	MoreInfoNeeded: 'More Info Needed',
	ContractSigned: 'Contract Signed',
	Pending: 'Ready for Launch',
	ReadyForReview: 'Submitted',
	Active: 'Deal Launched',
	ActiveClosing: 'Underwriting Diligence',
	TermSheetIssued: 'Term Sheets Issued',
	ClosedFunded: 'Closed-Funded',
	ClosedDiscarded: 'Closed-Lost',
	Paused: 'Paused',
	Inactive: 'Inactive',
};

export type DealStatus = keyof typeof DealStatuses;

export type NatureOfBuyer =
	| 'NotSelected'
	| 'ExistingOwner'
	| 'IndividualNonExistingOwner'
	| 'FundedSponsor'
	| 'NonFundedSponsor'
	| 'Strategic';

export type RunwayLeftInMonths = keyof typeof RunwayLeftInMonthsMap;

export const RunwayLeftInMonthsMap = {
	NotSelected: 'Select',
	Zero: 'None',
	One: '1 month or less',
	Two: '2 months',
	Three: '3 months',
	Four: '4 months',
	Five: '5 months',
	Six: '6 months',
	SevenToTwelve: '7 - 12 months',
	TwelvePlus: '12+ months',
	CashFlowPositive: 'Cash Flow Positive',
};

export type BusinessSalesType = keyof typeof BusinessSalesTypeMap;

export const BusinessSalesTypeMap = {
	NotSelected: 'Not Selected',
	B2B: 'Other Businesses - B2B',
	B2C: 'Consumers - B2C',
	B2G: 'Government Entities - B2G',
};

export const CurrencyTypeMap = {
	NotSelected: 'Not Selected',
	Us: 'USD',
	Uk: 'GBP',
	Au: 'AUD',
	Ca: 'CAD',
	Eu: 'EUR',
	Hk: 'HKD',
};

export type TransactionType = 'None' | 'Asset' | 'StockSale';

export type FinancialStatementType = keyof typeof FinancialStatementTypeMap;

export const FinancialStatementTypeMap = {
	None: 'None',
	Audited: 'Audited',
	Reviewed: 'Reviewed',
	Compiled: 'Compiled',
	CompanyPrepared: 'Company Prepared',
	TaxReturns: 'Tax Returns',
};

export type EvaluationStage = 'None' | 'LoiSigned' | 'LoiSubmitted' | 'LoiNegotiation' | 'EarlyStages';

export type CurrencyType = 'USD' | 'GBP' | 'AUD' | 'CAD' | 'EUR' | 'HKD';

export const isDealActive = (deal: Deal | null | undefined): boolean => {
	if (!deal) return false;

	return (
		deal.dealStatus == 'Active' ||
		deal.dealStatus == 'ActiveClosing' ||
		deal.dealStatus == 'Paused' ||
		deal.dealStatus == 'ContractSigned' ||
		deal.dealStatus == 'TermSheetIssued'
	);
};

export const isDealPending = (deal: Deal | null | undefined): boolean => {
	if (!deal) return false;

	return (
		deal.dealStatus == 'ReadyForReview' ||
		deal.dealStatus == 'Started' ||
		deal.dealStatus == 'Accepted' ||
		deal.dealStatus == 'MoreInfoNeeded'
	);
};
